import React, { FunctionComponent } from 'react';
import { AppBar, Box, Button, CircularProgress, Fab, styled, Toolbar, Typography, useTheme } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { blueGrey } from '@mui/material/colors';
import ImageImports from '../../utils/ImageImports';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCheckMaintenanceMode } from '../../customHooks/MaintenanceModeHttpService';

const MaintenanceContainer: FunctionComponent = () => {
  const theme = useTheme();
  const { ugLogo } = ImageImports;
  const H1 = styled(Typography)(({ theme }) => ({
    fontSize: '1.75rem',
    lineHeight: '2.125rem',
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem'
    }
  }));

  const Paragraph = styled(Typography)(({ theme }) => ({
    fontSize: '1.25rem',
    lineHeight: '1.75rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem'
    }
  }));

  const { isLoading, isFetching, refetch } = useCheckMaintenanceMode({
    enabled: true,
    onSuccess(data) {
      if (data.maintenanceMode === false && localStorage.getItem('maintenanceMode') === 'true') {
        localStorage.removeItem('maintenanceMode');
        window.location.reload();
      }
    },
    onError(error) {
      console.error('Error fetching announcements data:', error);
    }
  });

  const LoadingCircle: React.FC = () => (
    <CircularProgress
      size={50}
      sx={{
        color: theme.palette.secondary.main,
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: '-12px',
        marginLeft: '-12px'
      }}
    />
  );

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="fixed"
        sx={{ backgroundColor: theme.palette.background.paper, boxShadow: theme.shadows[3] }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <img
              src={ugLogo}
              className="App-logo ml-0 lg:ml-6"
              alt="logo"
            />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <Button
              sx={{
                fontWeight: 700,
                width: { xs: '100%', md: 'auto' },
                borderRadius: '96px',
                fontSize: '0.875rem',
                px: '32px',
                py: { xs: '10px', md: '6px' },
                letterSpacing: '0em',
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none'
                }
              }}
              variant="contained"
              endIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <Fab
              color="primary"
              aria-label="refresh page"
              size="small"
              onClick={() => refetch()}
            >
              <RefreshIcon fontSize="small" />
            </Fab>
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ backgroundColor: blueGrey[50], height: '100%', flexGrow: 1 }}>
        <section className="min-h-screen grid place-items-center pt-[30px] md:pt-[60px]">
          <div className="container mx-auto">
            {isLoading || isFetching ? (
              <LoadingCircle />
            ) : (
              <div className="text-center p-5 my-1 md:my-5">
                <Box sx={{ fontSize: { xs: '3rem', sm: '6rem' } }}>
                  <SettingsOutlinedIcon
                    fontSize="inherit"
                    className={`mx-auto text-orange-500`}
                  />
                </Box>
                <div className="my-6 max-w-xl mx-auto [text-wrap:_balance]">
                  <H1>We&apos;re currently undergoing maintenance to improve your experience.</H1>
                </div>

                <div className="text-foreground max-w-xl mx-auto [text-wrap:_balance]">
                  <Paragraph>Please bear with us while we make these enhancements. We&apos;ll be back shortly. Thank you for your patience!</Paragraph>
                </div>
              </div>
            )}
          </div>
        </section>
      </Box>
    </Box>
  );
};

export default MaintenanceContainer;
